import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import {
  LANGUAGE_NAMES,
  SUPPORTED_LANGUAGES,
  type Language,
} from "@/i18n";

/**
 * LanguageSwitcher — a compact globe + native `<select>` that switches
 * the app language and persists the choice (i18n's language detector
 * writes it to `localStorage`).
 *
 * A native `<select>` keeps the control fully keyboard- and
 * screen-reader-accessible for free, and it renders the OS-native
 * option list. The globe glyph and chevron are layered over it; the
 * select itself is transparent and sits on top to stay interactive.
 */
export function LanguageSwitcher({ style }: { style?: CSSProperties }) {
  const { i18n, t } = useTranslation();
  const current = (SUPPORTED_LANGUAGES as readonly string[]).includes(
    i18n.resolvedLanguage ?? "",
  )
    ? (i18n.resolvedLanguage as Language)
    : "en";

  return (
    <label
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        height: 32,
        padding: "0 26px 0 10px",
        borderRadius: "var(--radius-pill)",
        background: "var(--surface-card)",
        border: "1px solid var(--border-subtle)",
        color: "var(--text-strong)",
        cursor: "pointer",
        ...style,
      }}
    >
      <Icon name="globe" size={16} color="var(--text-muted)" />
      <span
        aria-hidden
        style={{
          font: "var(--fw-semibold) var(--fs-sm)/1 var(--font-body)",
          whiteSpace: "nowrap",
        }}
      >
        {LANGUAGE_NAMES[current]}
      </span>
      <span
        aria-hidden
        style={{
          position: "absolute",
          right: 10,
          color: "var(--text-faint)",
          font: "var(--fw-regular) var(--fs-2xs)/1 var(--font-body)",
          pointerEvents: "none",
        }}
      >
        ▾
      </span>
      <select
        aria-label={t("language.label")}
        value={current}
        onChange={(e) => i18n.changeLanguage(e.target.value)}
        data-testid="language-switcher"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          opacity: 0,
          cursor: "pointer",
          appearance: "none",
          border: "none",
        }}
      >
        {SUPPORTED_LANGUAGES.map((lng) => (
          <option key={lng} value={lng}>
            {LANGUAGE_NAMES[lng]}
          </option>
        ))}
      </select>
    </label>
  );
}
